import React from "react";
import { Page, Text, StyleSheet, View, Image } from "@react-pdf/renderer";
import { Header } from "../../components/Header";
import Footer from "../../components/Footer";
import {
  PmmGuardianSimulatorProps,
  PmmGuardianSimulatorResultProps,
} from "../../../../interfaces/interfaces";
import pmmImage from "../../../../utils/images/pmm.png";

const styles = StyleSheet.create({
  page: {
    paddingTop: "50px",
    fontSize: 14,
  },
  grid: {
    display: "flex",
    flexDirection: "row",
    marginTop: 30,
  },
  leftCol: {
    flex: 1,
    marginLeft: 50,
  },
  rightCol: {
    marginRight: 50,
    paddingLeft: 20,
    display: "flex",
    alignItems: "center",
  },
  toolImage: {
    width: 55,
  },
  sectionLabel: {
    fontSize: 11,
    fontFamily: "Times-Bold",
    marginBottom: 3,
    marginTop: 10,
  },
  sectionLabelRed: {
    fontSize: 11,
    fontFamily: "Times-Bold",
    color: "#C00000",
    marginBottom: 3,
    marginTop: 16,
  },
  table: {
    display: "flex",
    width: "auto",
  },
  row: {
    flexDirection: "row",
    borderLeft: "0.5px solid black",
    borderTop: "0.5px solid black",
    borderBottom: "0.5px solid black",
  },
  cellLabel: {
    flex: 3,
    borderRight: "0.5px solid black",
    paddingVertical: 3,
    paddingHorizontal: 4,
    fontFamily: "Times-Roman",
    fontSize: 10,
  },
  cellValue: {
    flex: 1,
    borderRight: "0.5px solid black",
    paddingVertical: 3,
    paddingHorizontal: 4,
    fontFamily: "Times-Roman",
    fontSize: 10,
    textAlign: "right",
  },
  cellValueRed: {
    flex: 1,
    borderRight: "0.5px solid black",
    paddingVertical: 3,
    paddingHorizontal: 4,
    fontFamily: "Times-Bold",
    fontSize: 10,
    textAlign: "right",
    backgroundColor: "#FF0000",
    color: "white",
  },
  cellUnit: {
    width: 28,
    borderRight: "0.5px solid black",
    paddingVertical: 3,
    paddingHorizontal: 4,
    fontFamily: "Times-Roman",
    fontSize: 10,
  },
  notesText: {
    fontSize: 10,
    fontFamily: "Times-Roman",
    marginTop: 12,
    lineHeight: 1.5,
  },
  notesBold: {
    fontFamily: "Times-Bold",
    fontSize: 10,
  },
  notesBody: {
    fontSize: 10,
    fontFamily: "Times-Roman",
    marginTop: 10,
    lineHeight: 1.5,
  },
});

interface Props {
  data: PmmGuardianSimulatorProps;
  results: PmmGuardianSimulatorResultProps;
}

export const PmmGuardianSimulatorPage = ({ data, results }: Props) => {
  return (
    <Page style={styles.page}>
      <Header title="PMM Guardian" subtitle="" />

      <View style={styles.grid}>
        {/* Left column — tables + notes */}
        <View style={styles.leftCol}>
          {/* INPUT DATA */}
          <Text style={styles.sectionLabel}>INPUT DATA</Text>
          <View style={styles.table}>
            <View style={styles.row}>
              <Text style={styles.cellLabel}>Specific Gravity of Fluid</Text>
              <Text style={styles.cellValue}>{data.specificGravity}</Text>
              <Text style={styles.cellUnit}></Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.cellLabel}>Water Constant</Text>
              <Text style={styles.cellValue}>{data.waterConstant}</Text>
              <Text style={styles.cellUnit}></Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.cellLabel}>Intake Pressure</Text>
              <Text style={styles.cellValue}>{data.intakePressure}</Text>
              <Text style={styles.cellUnit}>psi</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.cellLabel}>PMM Depth</Text>
              <Text style={styles.cellValue}>{data.pmmDepth}</Text>
              <Text style={styles.cellUnit}>ft</Text>
            </View>
          </View>

          {/* OUTPUT DATA */}
          <Text style={styles.sectionLabelRed}>OUTPUT DATA</Text>
          <View style={styles.table}>
            <View style={styles.row}>
              <Text style={styles.cellLabel}>Hydrostatic Pressure</Text>
              <Text style={styles.cellValue}>{results.hydrostaticPressure}</Text>
              <Text style={styles.cellUnit}>psi</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.cellLabel}>Quantity of screws in the tool</Text>
              <Text style={styles.cellValueRed}>{results.screwQuantity}</Text>
              <Text style={styles.cellUnit}></Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.cellLabel}>Set Point (PMM GUARDIAN)</Text>
              <Text style={styles.cellValueRed}>{results.setPoint}</Text>
              <Text style={styles.cellUnit}>psi</Text>
            </View>
          </View>

          {/* Notes — inside left column, below tables */}
          <Text style={styles.notesText}>
            <Text style={styles.notesBold}>Notes: </Text>
            Therefore, if they had {data.intakePressure} psi intake pressure,
            it would take {results.surfacePressureZeroPip} psi at surface to
            pop it once the dart/ball is on seat. If the PIP is higher than 0
            psi it will take the surface pressure plus intake pressure. In this
            case {results.surfacePressureWithPip} psi.
          </Text>
          <Text style={styles.notesBody}>
            As you remove screws, take them out 180 degrees apart from each
            other so you don't end up with your remaining screws all on one
            side of the tool. If you add screws back in after they've been
            removed, you want to take them until they are tight then come back
            1/4 of a turn.
          </Text>
        </View>

        {/* Right column — tool image */}
        <View style={styles.rightCol}>
          <Image src={pmmImage} style={styles.toolImage} />
        </View>
      </View>

      <Footer />
    </Page>
  );
};
